import { backendUrl as defaultBackend } from "../config_prod";
import { Lang } from "../type/language";

type RequestParams = {
  backendUrl?: string;
  email: string;
  offerId: string;
  name: string;
  lang: Lang;
};

type VerifyParams = {
  backendUrl?: string;
  email: string;
  offerId: string;
  code: string;
};

type StatusParams = {
  backendUrl?: string;
  email: string;
  offerId: string;
};

type ConfirmPendingParams = {
  backendUrl?: string;
  email: string;
  offerId: string;
  token: string;
  eventId: string;
  lang: Lang;
};

export async function requestAccountVerificationCode({
  backendUrl = defaultBackend,
  email,
  offerId,
  name,
  lang,
}: RequestParams): Promise<{ required: boolean; expiresInMinutes?: number }> {
  const res = await fetch(`${backendUrl}/kscan/v1/ats/post/account-verification/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      opportunity_id: offerId,
      name,
      lang,
    }),
  });

  let data: any = {};
  try {
    data = await res.json();
  } catch {
    // no-op
  }

  if (!res.ok || data.status !== 200) {
    throw new Error(data?.message || `HTTP ${res.status}`);
  }

  return {
    required: Boolean(data?.data?.required),
    expiresInMinutes: data?.data?.expires_in_minutes,
  };
}

export async function verifyAccountCode({
  backendUrl = defaultBackend,
  email,
  offerId,
  code,
}: VerifyParams): Promise<string> {
  const res = await fetch(`${backendUrl}/kscan/v1/ats/post/account-verification/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      opportunity_id: offerId,
      code,
    }),
  });

  let data: any = {};
  try {
    data = await res.json();
  } catch {
    // no-op
  }

  if (!res.ok || data.status !== 200 || !data?.data?.verification_token) {
    throw new Error(data?.message || `HTTP ${res.status}`);
  }

  return String(data.data.verification_token);
}

export async function getAccountVerificationStatus({
  backendUrl = defaultBackend,
  email,
  offerId,
}: StatusParams): Promise<{ verified: boolean; verificationToken?: string; expired?: boolean }> {
  const res = await fetch(`${backendUrl}/kscan/v1/ats/post/account-verification/status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      opportunity_id: offerId,
    }),
  });

  let data: any = {};
  try {
    data = await res.json();
  } catch {
    // no-op
  }

  if (!res.ok || (data.status !== 200 && data.status !== 404)) {
    throw new Error(data?.message || `HTTP ${res.status}`);
  }

  return {
    verified: Boolean(data?.data?.verified),
    verificationToken: data?.data?.verification_token ? String(data.data.verification_token) : undefined,
    expired: Boolean(data?.data?.expired),
  };
}

export async function confirmPendingApplication({
  backendUrl = defaultBackend,
  email,
  offerId,
  token,
  eventId,
  lang,
}: ConfirmPendingParams): Promise<void> {
  const res = await fetch(`${backendUrl}/kscan/v1/ats/post/candidate/inscription/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      opportunity_id: offerId,
      token,
      event_id: eventId,
      lang,
    }),
  });

  let data: any = {};
  try {
    data = await res.json();
  } catch {
    // no-op
  }

  if (!res.ok || data.status !== 200) {
    throw new Error(data?.message || `HTTP ${res.status}`);
  }
}
